<template>
  <div v-if="pipeline" class="pt-4 flex flex-1 flex-col h-full">
    <div class="mb-4 px-6 flex justify-between">
      <div class="w-full">
        <div class="flex items-center mb-4">
          <button v-if="routeFrom" @click="$router.back()" class="btn btn-brand pl-2">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              class="inline-block fill-current -ml-2 mr-2"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14.71 6.70999C14.6175 6.61728 14.5076 6.54373 14.3866 6.49355C14.2657
                      6.44337 14.136 6.41754 14.005 6.41754C13.874 6.41754 13.7444 6.44337
                      13.6234 6.49355C13.5024 6.54373 13.3925 6.61728 13.3 6.70999L8.71
                      11.3C8.6173 11.3925 8.54375 11.5024 8.49357 11.6234C8.44339 11.7443
                      8.41756 11.874 8.41756 12.005C8.41756 12.136 8.44339 12.2656 8.49357
                      12.3866C8.54375 12.5076 8.6173 12.6175 8.71 12.71L13.3 17.3C13.3926
                      17.3926 13.5025 17.466 13.6235 17.5161C13.7444 17.5662 13.8741 17.592
                      14.005 17.592C14.1359 17.592 14.2656 17.5662 14.3865 17.5161C14.5075
                      17.466 14.6174 17.3926 14.71 17.3C14.8026 17.2074 14.876 17.0975 14.9261
                      16.9765C14.9762 16.8556 15.002 16.7259 15.002 16.595C15.002 16.4641
                      14.9762 16.3344 14.9261 16.2134C14.876 16.0925 14.8026 15.9826 14.71
                      15.89L10.83 12L14.71 8.11999C15.1 7.72999 15.09 7.08999 14.71
                      6.70999V6.70999Z"
              />
            </svg>
            <span>Back</span>
          </button>
          <div class="flex items-center ml-auto space-x-2">
            <InfoModal v-if="pipeline.description" :content="pipeline.description" html>
              <template #button="{ setShow }">
                <BaseButton info @click="setShow(true)">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <circle cx="12" cy="12" r="9" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                    <polyline points="11 12 12 12 12 16 13 16" />
                  </svg>
                </BaseButton>
              </template>
            </InfoModal>
            <ProjectFilter
              :pipeline-id="pipelineUuid"
              :account-id="pipeline.accountId"
              v-model="projectId"
            />
            <div class="flex items-center">
              <label class="relative">
                <input
                  v-model="assignedToMe"
                  :value="false"
                  type="radio"
                  name="assigned"
                  class="field opacity-0 absolute inset-0 w-full h-full"
                  @input="$emit('update:assigned-to-me', false)"
                />
                <span
                  class="inline-block px-3 py-2 bg-white leading-6 rounded-l field-checked:bg-blue-600 field-checked:text-white transition-colors duration-75"
                >
                  All
                </span>
              </label>
              <label class="relative">
                <input
                  v-model="assignedToMe"
                  :value="true"
                  type="radio"
                  name="assigned"
                  class="field opacity-0 absolute inset-0 w-full h-full"
                  @input="$emit('update:assigned-to-me', true)"
                />
                <span
                  class="inline-block px-3 py-2 bg-white leading-6 rounded-r field-checked:bg-blue-600 field-checked:text-white transition-colors duration-75"
                >
                  Assigned to me
                </span>
              </label>
            </div>
            <BaseButton
              v-if="$membershipHasPermissionTo('edit pipelines')"
              tag="router-link"
              :to="{
                name: 'accounts.pipelines.pipelineUuid.settings',
                params: {
                  accountId,
                  pipelineUuid,
                },
              }"
              primary
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                class="w-5 h-5"
              >
                <path fill="none" d="M0 0h24v24H0z" />
                <path
                  d="M2 12c0-.865.11-1.703.316-2.504A3 3 0 0 0 4.99
                  4.867a9.99 9.99 0 0 1 4.335-2.505 3 3 0 0 0 5.348
                  0 9.99 9.99 0 0 1 4.335 2.505 3 3 0 0 0 2.675
                  4.63c.206.8.316 1.638.316 2.503 0 .865-.11 1.703-.316
                  2.504a3 3 0 0 0-2.675 4.629 9.99 9.99 0 0 1-4.335
                  2.505 3 3 0 0 0-5.348 0 9.99 9.99 0 0 1-4.335-2.505
                  3 3 0 0 0-2.675-4.63C2.11 13.704 2 12.866 2 12zm4.804
                  3c.63 1.091.81 2.346.564 3.524.408.29.842.541
                  1.297.75A4.993 4.993 0 0 1 12 18c1.26 0 2.438.471
                  3.335 1.274.455-.209.889-.46 1.297-.75A4.993 4.993
                  0 0 1 17.196 15a4.993 4.993 0 0 1 2.77-2.25 8.126
                  8.126 0 0 0 0-1.5A4.993 4.993 0 0 1 17.195 9a4.993
                  4.993 0 0 1-.564-3.524 7.989 7.989 0 0 0-1.297-.75A4.993
                  4.993 0 0 1 12 6a4.993 4.993 0 0 1-3.335-1.274 7.99
                  7.99 0 0 0-1.297.75A4.993 4.993 0 0 1 6.804 9a4.993
                  4.993 0 0 1-2.77 2.25 8.126 8.126 0 0 0 0 1.5A4.993
                  4.993 0 0 1 6.805 15zM12 15a3 3 0 1 1 0-6 3 3 0 0 1
                  0 6zm0-2a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"
                />
              </svg>
            </BaseButton>
          </div>
        </div>
        <h4 class="text-indigo-500">
          {{ pipeline.name }}
        </h4>
      </div>
    </div>
    <Pipeline
      v-if="pipeline"
      :pipeline="pipeline"
      :assigned-to-me="assignedToMe"
      :project-id="projectId"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import InfoModal from 'Components/InfoModal.vue';
import { middleware } from '@/routes/guards';
import Pipeline from '@/components/Pipelines/Pipeline.vue';
import { pipelinesClient } from '@/api/clients';
import ProjectFilter from './_pipelineUuid/components/ProjectFilter.vue';

export default {
  components: {
    Pipeline,
    InfoModal,
    ProjectFilter,
  },

  props: {
    pipelineUuid: {
      type: String,
      default: null,
    },
    accountId: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      routeFrom: null,
      loading: false,
      assignedToMe: false,
      projectId: null,
      pipeline: null,
    };
  },

  computed: {
    ...mapState('entities', {
      project({ projects }) {
        if (!this.projectId) return null;

        return projects.byId[this.projectId];
      },
    }),
  },

  mounted() {
    if (this.$route.query.contact_request_id) {
      this.$modal.show({
        component: 'ContactRequestModal',
        props: {
          contactRequestId: this.$route.query.contact_request_id,
        },
        on: {
          hide: () => {
            this.$emit('edited', this.$route.query.contact_request_id);
          },
        },
        hideEvent: 'hide',
      });
    }
  },

  created() {
    this.init();
  },

  beforeRouteEnter(to, from, next) {
    const check = middleware('account-permission:read pipelines');

    check(to, from, async (args) => {
      if (typeof args !== 'undefined') {
        return next(...args);
      }

      return next((vm) => {
        if (from?.name !== 'accounts.pipelines.pipelineUuid.settings') {
          vm.setRouteFrom(from);
        }
      });
    });
  },

  methods: {
    setRouteFrom(routeFrom) {
      this.routeFrom = routeFrom;
    },
    async init() {
      this.loading = true;

      try {
        await this.fetchPipeline();
      } catch (error) {
        //
      } finally {
        this.loading = false;
      }
    },
    fetchPipeline() {
      return pipelinesClient
        .read(this.pipelineUuid, {
          params: {
            include: 'pipelineStages',
          },
        })
        .then((response) => {
          this.pipeline = response.data.data;
        });
    },
    clearRouteQuery() {
      const {
        name,
        params,
        query: { editContact, ...restQuery },
      } = this.$route;

      if (!editContact) return;

      this.$router.replace({ name, params: { ...params }, query: { ...restQuery } });
    },
  },
};
</script>
